import { ICreateContractFromTemplateForm } from 'services/contract/types';
import {
  IAddress,
  IInfinitePageParams,
  IInfinitePageResponse,
  STATUS,
} from 'services/types';

export interface ILabelMutationAddress extends IAddress {}
export interface ILabelMutationTaxOffice extends IAddress {
  name: string;
}

export interface ILabelMutation {
  labelStatus: LABEL_STATUS;
  labelName: string;
  email: string;
  phoneNumberPrefix: string;
  phoneNumber: string;
  customerType: CUSTOMER_TYPE;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  address: ILabelMutationAddress;
  taxOffice: ILabelMutationTaxOffice;
  bankAccountNumber: string;
  bankName: string;
  swift: string;
  personalIdCountryAlfa2: string;
  personalId: string;
  companyId: string;
  companyCountryAlfa2: string;
  legalSupervisorFirstName: string;
  legalSupervisorLastName: string;
  legalSupervisorCountryAlfa2: string;
  legalSupervisorPersonalId: string;
  notifyOnDocumentGeneration: boolean;
  settlementMethod: SETTLEMENT_METHOD | null;
  settlementDeductionPercentage: number | null;
  routingNumber: string | null;
  notes: string | null;
  trustLevel: TRUST_LEVEL;
}

export interface ICreateLabel extends ILabelMutation {}

export interface IEditLabel extends ILabelMutation {
  id: string;
}

export interface ISetDefaultContractParams {
  labelId: string;
  productType: number;
  contractId: string;
}

export interface IDeleteDefaultContractParams {
  labelId: string;
  contractId: string;
}

export interface ILabelResidenceCertificateMutation {
  LabelId: string;
  ResidenceCertificateCountryCodeAlfa2: string;
  ResidenceCertificateFile: File;
  ResidenceCertificateExpirationDate: string;
}


export interface ILabelForm extends Omit<ILabelMutation, 'settlementMethod'> {
  defaultContracts: ILabelDefaultContract[];

  residenceCertificates: ILabelResidenceCertificateResponse[];
  residenceCertificate?: ILabelResidenceCertificateMutation;
  residenceCertificateExpirationStatus?: {
    name: string;
    id: number;
  };
  contractFromTemplate?: ICreateContractFromTemplateForm;
  settlementMethod: SETTLEMENT_METHOD;
  settlementDeductionPercentage: number | null;
  reports?: {
    generate?: {
      startSettlementPeriodId: string;
      endSettlementPeriodId: string;
      planedStartDate: string;
      asap: boolean;
    };
    search?: {
      startSettlementPeriodId: string;
      endSettlementPeriodId: string;
    };
  };
  settlement?: {
    generate?: {
      startSettlementPeriodId: string;
      endSettlementPeriodId: string;
      planedSettlementDate: string;
      asap: boolean;
    };
    generateToNewest?: {
      planedSettlementDate: string;
      asap: boolean;
      dividePeriods: boolean;
    };
    search?: {
      startSettlementPeriodId: string;
      endSettlementPeriodId: string;
    };
  };
}

export interface IGetLabelListInfiniteParams extends IInfinitePageParams {
  labelName?: string;
  personalId?: string;
  companyId?: string;
  email?: string;
  status?: STATUS;
  showExpiring?: boolean;
  customerTypeId?: CUSTOMER_TYPE;
  hasContracts?: boolean;
}

export interface ILabelResidenceCertificateCountryResponse {
  polishName: string;
  englishName: string;
  codeAlfa2: string;
  codeAlfa3: string;
}

export interface ILabelResidenceCertificateVatTaxResponse {
  vatTax: number;
  codeAlfa2: string;
}

export interface ILabelResidenceCertificateScanResponse {
  id: string;
  fileName: string;
  path: string;
  sizeInMB: number;
  createdAt: string;
  extension: string;
  mD5Hash: string;
}

export interface ILabelResidenceCertificateResponse {
  id: string;
  country: ILabelResidenceCertificateCountryResponse;
  countryVatTax: ILabelResidenceCertificateVatTaxResponse;
  residenceScan: ILabelResidenceCertificateScanResponse;
  expirationDate: string;
}

export interface ILabelDefaultContract {
  contractId: string;
  productType: {
    name: string;
    id: number;
  };
}

export interface ILabelResponse {
  accountDetailsBankAccountNumber: string;
  accountDetailsBankName: string;
  accountDetailsSwift?: string;
  addressCity: string;
  addressCountry: string;
  addressFlatNumber: string;
  addressHouseNumber: string;
  addressStreet: string;
  addressZipCode: string;
  companyIdCompanyIdentification: string | null;
  companyIdCountry: string | null;
  createdAt: string | null;
  customerTypeId: CUSTOMER_TYPE;
  dateOfBirth: string;
  editedAt: string | null;
  accountDetailsRoutingNumber: string | null;
  email: string;
  firstName: string;
  id: string;
  isOfLegalAgeValue: string | null;
  labelName: string;
  labelStatus: LABEL_STATUS;
  lastName: string;
  legalSupervisorFirstName: string | null;
  legalSupervisorLastName: string | null;
  legalSupervisorPersonalId: string | null;
  legalSupervisorPersonalIdCountry: string | null;
  personalId: string;
  personalIdCountry: string;
  phoneNumberNumber: string;
  phoneNumberPrefix: string;
  taxOfficeAddressCity: string;
  taxOfficeAddressCountry: string;
  taxOfficeAddressFlatNumber: string;
  taxOfficeAddressHouseNumber: string;
  taxOfficeAddressStreet: string;
  taxOfficeAddressZipCode: string;
  taxOfficeName: string;
  notifyOnDocumentGeneration: boolean | null;
  residenceCertificates: ILabelResidenceCertificateResponse[];
  residenceCertificateExpirationStatus: {
    name: string;
    id: number;
  };
  settlementMethodId: SETTLEMENT_METHOD | null;
  settlementMethodName: string | null;
  settlementDeductionPercentage?: number | null;
  notes: string | null;
  trustLevel: TRUST_LEVEL;
  defaultContracts: ILabelDefaultContract[];
}

export interface ILabelPageResponse
  extends IInfinitePageResponse<ILabelResponse> {}

export interface ILabelLight {
  id: string;
  labelName: string;
}

export enum CUSTOMER_TYPE {
  unknown = -1,
  Physical = 1,
  Company = 2,
}

export enum SETTLEMENT_METHOD {
  unknown = -1,
  greedy = 1,
  generous = 2,
  alwaysMoney = 3,
}

export enum RESIDENCE_CERTIFICATE_EXPIRATION_STATUS {
  NoExpirationOrNoCertificates = 1,
  ExpiringSoon = 2,
  Expired = 3,
}

export enum LABEL_STATUS {
  Draft = 1,
  NotVerified = 2,
  DuringVerification = 3,
  Verified = 4,
  Banned = 5,
}

export enum TRUST_LEVEL {
  Untrusted = 1,
  PartiallyTrustedProducts = 2,
  PartiallyTrustedDeliveries = 3,
  Trusted = 4,
}

export interface ICheckSettlementPeriodsParams {
  labelId: string;
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
}

export interface ICheckSettlementPeriodsResponse {
  hasOverlap: boolean;
  hasGap: boolean;
  labelReportOverlap: boolean;
}

export interface ILabelTopProduct {
  productId: string;
  title: string;
  earnedAmount: number;
  productType: {
    name: string;
    id: number;
  };
}

export interface ILabelTopProductsResponse {
  items: ILabelTopProduct[];
  totalPageCount: number;
}

export interface IGetLabelTopProductsParams extends IInfinitePageParams {
  labelId: string;
  startSettlementPeriodId?: string;
  endSettlementPeriodId?: string;
}

export interface ILabelEarnings {
  earnedAmount: number;
  settlementPeriodId: string;
  settlementPeriod: {
    name: string;
    year: number;
    startDate: string;
    endDate: string;
  };
}

export interface ILabelEarningsResponse {
  earnings: ILabelEarnings[];
  totalAmount: number;
}

export interface IValidateLabelAccountParams {
  bankAccountNumber: string;
  swift: string;
  countryAlfa2: string;
  routingNumber: string;
}

export interface IUpdateLabelByTokenParams {
  token: string;
  email: string;
  phoneNumberPrefix: string;
  phoneNumber: string;
  customerType: CUSTOMER_TYPE;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  address: ILabelMutationAddress;
  taxOffice: ILabelMutationTaxOffice;
  legalSupervisorFirstName: string;
  legalSupervisorLastName: string;
  legalSupervisorCountryAlfa2: string;
  legalSupervisorPersonalId: string;
  bankAccountNumber: string;
  bankName: string;
  swift: string;
  personalIdCountryAlfa2: string;
  personalId: string;
  companyId: string;
  companyCountryAlfa2: string;
  routingNumber: string | null;
}